// DisclosureLadder — vertical disclosure-ladder rung stepper for the Matches
// tab (mockup Fig38). Live previously showed only a flat "N overlaps / X%
// strength" summary with a plain agreement list; this renders the full six-rung
// ladder (Private → ... → Full Collaboration), a "you are here" marker, per-rung
// "would reveal" copy, and a Matched-partners list with per-partner
// Consent/Decline/Request-advance actions.
//
// Reuses the canonical level metadata already established elsewhere in the app
// (STAGES colours from graph/DisclosureLevel, "would reveal" copy from
// graph/DisclosureRequestModal's STAGE_INFO) instead of redefining it, and
// hands off the NDA-signing flow and full agreement history to the existing,
// well-tested DisclosureRequestModal rather than re-implementing that logic.
//
// Perspective (my/counterparty consented level) comes straight from the
// backend-computed `my_consented_level` / `counterparty_consented_level`
// fields on DisclosureAgreement (Progressive Disclosure Engine) — no org-id
// comparison is needed for the modern data shape, so this component has no
// dependency on AuthContext and stays trivially testable.
import { useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, CheckCircle2, MapPin, Undo2, ShieldCheck, Hourglass } from 'lucide-react'
import {
  getDisclosureAgreements,
  getDisclosureAgreementContent,
  consentDisclosureAdvance,
  declineDisclosureAdvance,
  requestDisclosureAdvance,
  revokeDisclosureLevel,
} from '../../services/api'
import type { DisclosureAgreement, DisclosureContent } from '../../types'
import { STAGES } from '../graph/DisclosureLevel'
import { STAGE_INFO } from '../graph/DisclosureRequestModal'
import DisclosureRequestModal from '../graph/DisclosureRequestModal'
import ConfirmDialog from '../shared/ConfirmDialog'
import HelpPopover from '../help/HelpPopover'

const MAX_LEVEL = 6

function rungName(level: number): string {
  return STAGE_INFO[level]?.name ?? STAGES.find((s) => s.level === level)?.name ?? `Level ${level}`
}

/** Perspective derived from the backend-computed consent fields, falling back
 * to current_level (i.e. "in sync, nothing pending") when a legacy agreement
 * carries neither field. */
function perspectiveOf(a: DisclosureAgreement) {
  const myConsented = a.my_consented_level ?? a.current_level
  const counterpartyConsented = a.counterparty_consented_level ?? a.current_level
  return {
    myConsented,
    counterpartyConsented,
    iAmAhead: myConsented > counterpartyConsented,
    theyAreAhead: counterpartyConsented > myConsented,
  }
}

function partnerLabel(a: DisclosureAgreement): string {
  if (a.counterparty_name) return a.counterparty_name
  if (a.counterparty_email) return a.counterparty_email
  return `Partner #${a.agreement_id.slice(0, 8)}`
}

/** What's actually NEW at each rung, in filters.STAGE_FIELDS order (the same
 * per-level breakdown the backend whitelist documents) — so a reached rung
 * shows the real fields it unlocked, not a repeat of the whole payload. */
function fieldsRevealedAt(level: number, c: DisclosureContent | undefined): Array<{ label: string; value: string }> {
  if (!c) return []
  const out: Array<{ label: string; value: string }> = []
  const pct = (n?: number) => (n === undefined ? undefined : `${Math.round(n * 100)}%`)
  if (level === 1) {
    if (c.similarity_band) out.push({ label: 'Similarity', value: `${c.similarity_band} band` })
  } else if (level === 2) {
    if (c.domain_category) out.push({ label: 'Domain', value: c.domain_category })
    if (c.aggregate_similarity_score !== undefined) out.push({ label: 'Match score', value: pct(c.aggregate_similarity_score) ?? '' })
    if (c.topic_overlap_count !== undefined) out.push({ label: 'Overlapping topics', value: String(c.topic_overlap_count) })
  } else if (level === 3) {
    if (c.org_sector) out.push({ label: 'Sector', value: c.org_sector })
    if (c.technology_domain) out.push({ label: 'Technology domain', value: c.technology_domain })
    if (c.capability_categories?.length) out.push({ label: 'Capabilities', value: c.capability_categories.join(' · ') })
  } else if (level === 4) {
    if (c.org_name) out.push({ label: 'Organisation', value: c.org_name })
    if (c.topics?.length) out.push({ label: 'Topics', value: c.topics.slice(0, 3).join(' · ') })
    if (c.complementarity_score !== undefined) out.push({ label: 'Complementarity', value: pct(c.complementarity_score) ?? '' })
  } else if (level === 5) {
    if (c.ai_summary) out.push({ label: 'AI summary', value: c.ai_summary })
    if (c.key_claims_redacted?.length) out.push({ label: 'Key claims', value: c.key_claims_redacted.join(' · ') })
  } else if (level >= 6) {
    if (c.contact_details?.name || c.contact_details?.email) {
      out.push({ label: 'Contact', value: [c.contact_details?.name, c.contact_details?.email].filter(Boolean).join(' · ') })
    }
  }
  return out
}

/** Matched-partners panel summary — real disclosed content for the
 * agreement's current level, NOT the bare level name (that was the bug:
 * "Them: Categorical Profile" repeating the rung name instead of showing
 * what the rung actually reveals). Content is a single shared payload per
 * agreement (the backend doesn't expose separate my-view/their-view
 * payloads), so this is the same summary regardless of which side is
 * asking — the my/their distinction lives in the Consent/Decline/Awaiting
 * controls below this line, not in the text itself. */
function partnerContentSummary(level: number, content: DisclosureContent | undefined): string {
  if (level <= 0) return 'Not yet matched'
  const revealed = fieldsRevealedAt(level, content)
  if (revealed.length > 0) {
    return revealed.slice(0, 2).map((f) => `${f.label}: ${f.value}`).join(' · ')
  }
  return content === undefined ? 'Loading…' : `Level ${level} of ${MAX_LEVEL} — no analysis data available yet`
}

interface DisclosureLadderProps {
  projectId: string
  discoverable: boolean
  matchRunComplete: boolean
}

export default function DisclosureLadder({ projectId, discoverable, matchRunComplete }: DisclosureLadderProps) {
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [confirmAdvance, setConfirmAdvance] = useState<{ agreement: DisclosureAgreement; nextLevel: number; consenting: boolean } | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<{ agreement: DisclosureAgreement; level: number } | null>(null)
  const [modalAgreement, setModalAgreement] = useState<DisclosureAgreement | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const enabled = !!projectId && discoverable && matchRunComplete
  const { data: agreements = [] } = useQuery<DisclosureAgreement[]>({
    queryKey: ['disclosureAgreements', projectId],
    queryFn: () => getDisclosureAgreements(projectId),
    enabled,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['disclosureAgreements', projectId] })

  // One content fetch per matched-partner agreement (not just the selected
  // one) — the Matched-partners panel below lists every agreement and needs
  // real content for each row, not just whichever is currently focused.
  // Same queryKey the rung list's lookup uses, so selecting an agreement
  // never re-fetches — it's already warm from this list.
  const contentQueries = useQueries({
    queries: agreements.map((a) => ({
      queryKey: ['disclosureAgreementContent', a.agreement_id],
      queryFn: () => getDisclosureAgreementContent(a.agreement_id),
      enabled: !!a.agreement_id,
    })),
  })
  const contentByAgreement = new Map<string, DisclosureContent | undefined>()
  agreements.forEach((a, i) => contentByAgreement.set(a.agreement_id, contentQueries[i]?.data?.content))

  const consentMutation = useMutation({
    mutationFn: (agreementId: string) => consentDisclosureAdvance(agreementId),
    onSuccess: () => { setActionError(null); setConfirmAdvance(null); invalidate() },
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : 'Failed to consent to the advance.'),
  })
  const requestMutation = useMutation({
    mutationFn: (agreementId: string) => requestDisclosureAdvance({ agreementId }),
    onSuccess: () => { setActionError(null); setConfirmAdvance(null); invalidate() },
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : 'Failed to request the advance.'),
  })
  const declineMutation = useMutation({
    mutationFn: (agreementId: string) => declineDisclosureAdvance(agreementId),
    onSuccess: () => { setActionError(null); invalidate() },
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : 'Failed to decline the advance.'),
  })
  const revokeMutation = useMutation({
    mutationFn: ({ agreementId, level }: { agreementId: string; level: number }) => revokeDisclosureLevel(agreementId, level),
    onSuccess: () => { setActionError(null); setRevokeTarget(null); invalidate() },
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : 'Failed to revoke to that level.'),
  })

  // Nothing to show: MatchesTab already renders the "Express Interest" CTA and
  // the pipeline-stage instructions when matching isn't ready yet, so this
  // component stays fully silent rather than showing an empty ladder.
  if (!enabled || agreements.length === 0) return null

  const selected = agreements.find((a) => a.agreement_id === selectedId) ?? agreements[0]
  const content = contentByAgreement.get(selected.agreement_id)
  const { iAmAhead, theyAreAhead } = perspectiveOf(selected)
  const atMax = selected.current_level >= MAX_LEVEL
  const nextLevel = selected.current_level + 1
  const ndaBlocked = !atMax && nextLevel === 4 && selected.nda_status !== 'completed'

  const openAdvanceFlow = () => {
    if (ndaBlocked) { setModalAgreement(selected); return }
    setConfirmAdvance({ agreement: selected, nextLevel, consenting: theyAreAhead })
  }

  const runAdvance = () => {
    if (!confirmAdvance) return
    if (confirmAdvance.consenting) consentMutation.mutate(confirmAdvance.agreement.agreement_id)
    else requestMutation.mutate(confirmAdvance.agreement.agreement_id)
  }

  return (
    <div className="light-card p-4" data-testid="disclosure-ladder">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold text-iflow-900">Disclosure ladder</h3>
          <HelpPopover topic="concept-disclosure-ladder" />
        </div>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-iflow-700 bg-iflow-50 border border-iflow-200 rounded-full">
          <MapPin size={12} /> You are here — rung {selected.current_level} of {MAX_LEVEL}
        </span>
      </div>

      {actionError && (
        <p className="mb-3 text-xs text-red-600" role="alert">{actionError}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Rung list */}
        <div className="lg:col-span-3 space-y-1" data-testid="rung-list">
          {STAGES.map((stage) => {
            const info = STAGE_INFO[stage.level]
            const passed = stage.level < selected.current_level
            const current = stage.level === selected.current_level
            const locked = stage.level > selected.current_level + 1
            const isNdaGate = stage.level === 4
            return (
              <div
                key={stage.level}
                className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${
                  current ? 'border-iflow-300 bg-iflow-50' : passed ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200'
                }`}
              >
                <div
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                    current
                      ? 'bg-iflow-600 text-white'
                      : passed
                        ? 'bg-emerald-500 text-white'
                        : locked
                          ? 'bg-gray-100 text-gray-400'
                          : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {passed ? <CheckCircle2 size={14} /> : stage.level}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800">{stage.level} · {stage.name}</span>
                    {isNdaGate && stage.level > selected.current_level && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-100 rounded">
                        <Lock size={9} /> NDA gate
                      </span>
                    )}
                    {locked && !isNdaGate && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 bg-gray-100 rounded">
                        <Lock size={9} /> Locked
                      </span>
                    )}
                    {current && (
                      <span className="text-[10px] font-semibold text-iflow-700 uppercase tracking-wide">You are here</span>
                    )}
                  </div>
                  {stage.level <= selected.current_level ? (
                    (() => {
                      const revealed = fieldsRevealedAt(stage.level, content)
                      return revealed.length > 0 ? (
                        <dl className="mt-1 space-y-0.5" data-testid={`revealed-level-${stage.level}`}>
                          {revealed.map((f) => (
                            <div key={f.label} className="flex items-start gap-1.5 text-xs">
                              <dt className="font-medium text-gray-500 shrink-0">{f.label}:</dt>
                              <dd className="text-gray-700 min-w-0">{f.value}</dd>
                            </div>
                          ))}
                        </dl>
                      ) : (
                        <p className="text-xs text-gray-400 mt-0.5 italic">
                          Unlocked — no analysis data available for this project yet.
                        </p>
                      )
                    })()
                  ) : (
                    <p className="text-xs text-gray-600 mt-0.5">
                      <span className="font-medium">Would reveal: </span>
                      {info?.disclosure ?? 'Additional detail unlocks at this rung.'}
                    </p>
                  )}
                  {passed && (
                    <button
                      type="button"
                      onClick={() => setRevokeTarget({ agreement: selected, level: stage.level })}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-red-600"
                    >
                      <Undo2 size={11} /> Revoke to here
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Matched partners */}
        <div className="lg:col-span-2 space-y-2" data-testid="matched-partners">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Matched partners</h4>
          {agreements.map((a) => {
            const p = perspectiveOf(a)
            const isSelected = a.agreement_id === selected.agreement_id
            const partnerAtMax = a.current_level >= MAX_LEVEL
            const partnerNext = a.current_level + 1
            const partnerNdaBlocked = !partnerAtMax && partnerNext === 4 && a.nda_status !== 'completed'
            return (
              <div
                key={a.agreement_id}
                className={`rounded-lg border p-3 cursor-pointer transition-colors ${isSelected ? 'border-iflow-300 bg-iflow-50/60' : 'border-gray-200 hover:bg-gray-50'}`}
                onClick={() => setSelectedId(a.agreement_id)}
              >
                <p className="text-sm font-medium text-gray-800 truncate">{partnerLabel(a)}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {partnerContentSummary(a.current_level, contentByAgreement.get(a.agreement_id))}
                </p>
                <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {partnerAtMax ? (
                    <button
                      type="button"
                      onClick={() => setModalAgreement(a)}
                      className="px-2.5 py-1 text-xs font-medium text-iflow-600 border border-iflow-200 rounded-lg hover:bg-iflow-50"
                    >
                      View
                    </button>
                  ) : p.theyAreAhead ? (
                    <>
                      <button
                        type="button"
                        onClick={() => (partnerNdaBlocked ? setModalAgreement(a) : consentMutation.mutate(a.agreement_id))}
                        disabled={consentMutation.isPending}
                        className="px-2.5 py-1 text-xs font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
                      >
                        Consent
                      </button>
                      <button
                        type="button"
                        onClick={() => declineMutation.mutate(a.agreement_id)}
                        disabled={declineMutation.isPending}
                        className="px-2.5 py-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Decline
                      </button>
                    </>
                  ) : p.iAmAhead ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-500 bg-gray-100 rounded-lg">
                      <Hourglass size={11} /> Awaiting them
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => (partnerNdaBlocked ? setModalAgreement(a) : requestMutation.mutate(a.agreement_id))}
                      disabled={requestMutation.isPending}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-iflow-600 hover:text-iflow-700 hover:bg-iflow-50 rounded-lg disabled:opacity-50"
                    >
                      Request <ShieldCheck size={12} />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Advance / hold footer for the focused agreement */}
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-3 flex-wrap">
        <p className="text-xs text-gray-500">
          {atMax
            ? 'Full Collaboration reached — this is the top rung.'
            : `Currently at ${rungName(selected.current_level)} · advance only when you're ready`}
        </p>
        {!atMax && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirmAdvance(null)}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Hold at this level
            </button>
            {iAmAhead ? (
              <button
                type="button"
                disabled
                title="You have requested this advance — your counterparty must consent."
                className="px-3 py-1.5 text-xs font-medium text-gray-400 bg-gray-100 rounded-lg cursor-not-allowed"
              >
                Awaiting counterparty
              </button>
            ) : (
              <button
                type="button"
                onClick={openAdvanceFlow}
                className="px-3 py-1.5 text-xs font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800"
              >
                {theyAreAhead ? `Consent to ${rungName(nextLevel)}` : `Advance to ${rungName(nextLevel)} →`}
              </button>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmAdvance}
        title={`${confirmAdvance?.consenting ? 'Consent to' : 'Advance to'} "${confirmAdvance ? rungName(confirmAdvance.nextLevel) : ''}"?`}
        message={
          confirmAdvance
            ? `This will make the rung-${confirmAdvance.nextLevel} detail visible to ${partnerLabel(confirmAdvance.agreement)} once they also reach it. ${STAGE_INFO[confirmAdvance.nextLevel]?.disclosure ?? ''}`
            : ''
        }
        confirmLabel={confirmAdvance?.consenting ? 'Confirm & consent' : 'Confirm & advance'}
        onConfirm={runAdvance}
        onCancel={() => setConfirmAdvance(null)}
      />

      <ConfirmDialog
        open={!!revokeTarget}
        title={`Revoke to "${revokeTarget ? rungName(revokeTarget.level) : ''}"?`}
        message="This drops the disclosure level for both organisations back to the selected rung. Your counterparty will need to re-consent to climb back up."
        confirmLabel="Revoke"
        variant="danger"
        onConfirm={() => revokeTarget && revokeMutation.mutate({ agreementId: revokeTarget.agreement.agreement_id, level: revokeTarget.level })}
        onCancel={() => setRevokeTarget(null)}
      />

      {modalAgreement && (
        <DisclosureRequestModal
          isOpen={true}
          onClose={() => setModalAgreement(null)}
          agreement={modalAgreement}
          onConsent={invalidate}
          onReject={invalidate}
        />
      )}
    </div>
  )
}
